import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateAttribute, newCPMandPOM, syncScheduleEndDateWithExpiration } from "../../../../actions/flowActions";
import { Divider, FormControl, Paper, TextField } from "@material-ui/core";
import { IAppState } from "../../../../types/stores";
import { getFlowExportsByFlowItemId } from "../../../../reducers/flowExports";
import type { FlowExportsByItemId } from "../../../../reducers/flowExports";
import { createSelector } from "reselect";
import { FlowExport } from "../../../../types/stores/flowTypes";
import { parseDeliveryTypeId } from "../../../../helpers/parseDeliveryTypeId";
import moment from "moment";
import NumericalTextField from "../../../material-components/Misc/NumericalTextField";
import { IAdvertisersByCompany } from "../../../../types/stores/vars";
import { getFlowItemsForSelectedFlow } from "../../../../reducers/flowItems";
import { applyNumericValidation } from "../../../../reducers/flowExportSAATemplateFields";
import Button from "@material-ui/core/Button";
import {
    useReactTable,
    getCoreRowModel,
    getExpandedRowModel,
    flexRender,
    ColumnDef,
    ExpandedState,
} from "@tanstack/react-table";

let baseId = 0;

type Props = {
    flowExport: {
        ActivationChannelId: number;
        DeliveryTypeId: string;
    };
    flowItemId: number;
    saaTemplateFields: any;
    flow: any;
    flowItem: any;
    flowItems: any;
    maxVisibleCount: number;
    tableColumn: number;
    onTableReady?: () => void;
};

const FlowExportSAAFileBasedFields: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch();
    const getFlowExport = makeGetFlowExport();
    const getExportDestinations = makeGetExportDestinations();
    const flowExport = useSelector((state: IAppState) => getFlowExport(state, props));
    const environment = useSelector((state: IAppState) => state.session.environment);
    const flowExportSAATemplateFields = useSelector((state: IAppState) => state.flowExportSAATemplateFields);
    const currentCompanyId = useSelector((state: IAppState) => state.session.companyId);
    const companies = useSelector((state: IAppState) => state.session.companies);
    const PartnerList = useSelector((state: IAppState) => getExportDestinations(state));
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures);
    const reduxState = useSelector((state: IAppState) => state);
    const hasFieldRatePricing = enabledFeatures.includes("display-field-rates");
    const isImproveSAALayout = enabledFeatures.includes("improve-saa-field-layout");
    const [saveValidationWarnings, setSaveValidationWarnings] = useState<Record<string, string>>({});
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [renderingRows, setRenderingRows] = useState<Set<string>>(new Set());
    const [isTableReady, setIsTableReady] = useState(false);
    const localMaxVisibleCount = props.maxVisibleCount > 5 ? 5 : props.maxVisibleCount;
    const localMaxTableColumn = props.tableColumn || 2;

    const flowItems = useSelector(state => getFlowItemsForSelectedFlow(state));
    let filteredDestinations = PartnerList || [];
    // Build a stable form identifier used consistently for warnings
    const formId = useMemo(
        () => `${props.flowItemId}-${props.flowExport.ActivationChannelId}-${props.flowExport.DeliveryTypeId}`,
        [props.flowItemId, props.flowExport.ActivationChannelId, props.flowExport.DeliveryTypeId]
    );

    const validationMessages = useSelector(
        (state: IAppState) => state.saaValidationWarning.byForm[formId] || { warnings: {} }
    );

    //Only auprod and ukprod can see the grouped partners, others only the parent partners
    if (environment != "auprod" && environment != "ukprod" && PartnerList && PartnerList.length > 0) {
        filteredDestinations = PartnerList.filter(x => !x.isGrouped || x.PartnerAccessId == x.GroupCompanyID);
    }

    const destinations = filteredDestinations.map(item => ({
        label: item.VerticalName + " - " + item.CompanyName,
        value: item.PartnerAccessId,
        item,
    }));

    const activationChannelId =
        props.saaTemplateFields != null && props.saaTemplateFields.length > 0
            ? props.saaTemplateFields[0].SAATemplate.ActivationChannelID
            : "0";

    const thisDestination = destinations.find(x => x.value == flowExport?.DestinationId);

    // get CPM value
    const getCPMValue = useCallback(
        (parentFlowItem, flowItemId, parentFlowItemId) =>
            newCPMandPOM(reduxState, parentFlowItem, flowItemId, parentFlowItemId, hasFieldRatePricing, 1, false),
        [reduxState, hasFieldRatePricing]
    );

    const updateCPMField = (taxonomyObj, row, fieldName, cpmValue) => {
        const numericCPM = Number(cpmValue);
        const formattedCPM = numericCPM.toFixed(2);
        const prevCPMKey = `__prevCPM__${fieldName}`;
        const prevCPMValue = row[prevCPMKey];
        const currentValue = taxonomyObj[fieldName];

        const shouldSetDefault = !currentValue || currentValue === "";
        const hasChanged = typeof prevCPMValue !== "undefined" && prevCPMValue !== formattedCPM;

        if (shouldSetDefault || hasChanged) {
            taxonomyObj[fieldName] = formattedCPM;
            row[prevCPMKey] = formattedCPM;
            return true;
        } else {
            row[prevCPMKey] = formattedCPM;
            return false;
        }
    };

    // useEffect(() => {
    //     setSaveValidationWarnings(validationMessages.warnings);
    // }, [formId]);

    // NANI-85558 remove this method when feature is removed
    useEffect(() => {
        // Should execute new method
        if (enabledFeatures.includes("export-taxonomy-values-update-fix")) return;
        if (!props.saaTemplateFields || props.saaTemplateFields.length === 0) return;

        let saaObjects = Object.keys(flowExportSAATemplateFields.byId).map(x => flowExportSAATemplateFields.byId[x]);

        let saaFields = saaObjects.filter(x => x.FlowItemId == props.flowItemId);

        //updates taxonomy json with default values...
        saaFields.forEach(row => {
            const deliveryTypeId = parseDeliveryTypeId(flowExport?.DeliveryTypeId);
            if (row.TemplateId === deliveryTypeId) {
                let taxonomyObj = JSON.parse(row.ValueJSON);
                props.saaTemplateFields[0].SAASegmentFields.forEach(x => {
                    if (x.SAASegmentField.DefaultValue != null) {
                        if (x.SAASegmentField.DefaultValue.includes("{date:")) {
                            const dateFormat = x.SAASegmentField.DefaultValue.substring(
                                x.SAASegmentField.DefaultValue.indexOf(":") + 1,
                                x.SAASegmentField.DefaultValue.indexOf("}")
                            );
                            let newDate = moment();
                            let thisDate = moment(newDate, dateFormat).format(dateFormat).toString();
                            taxonomyObj[x.SAASegmentField.FieldName] = thisDate;
                        } else {
                            const company = companies.filter(z => z.CompanyID == currentCompanyId)[0];
                            const flow = props.flow;
                            const flowItem = props.flowItem;
                            const parentFlowItem = flowItems.find(x => x.FlowItemId == row.ParentFlowItemId);
                            if (x.SAASegmentField.DefaultValue.includes("{ProviderName}")) {
                                taxonomyObj[x.SAASegmentField.FieldName] = thisDestination?.item.CompanyFolder;
                            } else if (x.SAASegmentField.DefaultValue.includes("{CompanyName}")) {
                                taxonomyObj[x.SAASegmentField.FieldName] = company.CompanyFolder;
                            } else if (x.SAASegmentField.DefaultValue.includes("{FlowId}")) {
                                taxonomyObj[x.SAASegmentField.FieldName] = flow ? flow.FlowId : "";
                            } else if (x.SAASegmentField.DefaultValue.includes("{FlowItemId}")) {
                                taxonomyObj[x.SAASegmentField.FieldName] = flowItem.FlowItemId;
                            } else if (x.SAASegmentField.DefaultValue.includes("{FlowName}")) {
                                taxonomyObj[x.SAASegmentField.FieldName] = flow
                                    ? flow.FlowName.replace(/[^a-zA-Z0-9-_]/g, "").toUpperCase()
                                    : "";
                            } else if (x.SAASegmentField.DefaultValue.includes("{FlowItemName}")) {
                                taxonomyObj[x.SAASegmentField.FieldName] = flowItem.FlowItemName.replace(
                                    /[^a-zA-Z0-9-_]/g,
                                    ""
                                ).toUpperCase();
                            } else if (x.SAASegmentField.DefaultValue.includes("{ParentFlowItemName}")) {
                                taxonomyObj[x.SAASegmentField.FieldName] =
                                    taxonomyObj[x.SAASegmentField.FieldName] != ""
                                        ? taxonomyObj[x.SAASegmentField.FieldName]
                                        : parentFlowItem.FlowItemName.replace(/[^a-zA-Z0-9-_]/g, "").toUpperCase();
                            } else if (x.SAASegmentField.DefaultValue.includes("{CPM}")) {
                                const cpmValue = getCPMValue(parentFlowItem, row.FlowItemId, row.ParentFlowItemId);
                                const fieldName = x.SAASegmentField.FieldName;
                                updateCPMField(taxonomyObj, row, fieldName, cpmValue);
                            } else if (x.SAASegmentField.DefaultValue.includes("{ActivationChannelId}")) {
                                taxonomyObj[x.SAASegmentField.FieldName] = activationChannelId;
                            } else if (x.SAASegmentField.DefaultValue === "") {
                                // Do nothing
                            } else if (
                                !x.SAASegmentField.DefaultValue.includes("{") &&
                                taxonomyObj[x.SAASegmentField.FieldName] === ""
                            ) {
                                taxonomyObj[x.SAASegmentField.FieldName] = x.SAASegmentField.DefaultValue;
                            }
                        }
                    }
                });

                handleFieldChange(row.FlowExportSAATemplateFieldId, "ValueJSON", JSON.stringify(taxonomyObj));
            }
        });
    });

    useEffect(() => {
        // Should execute original method
        if (!enabledFeatures.includes("export-taxonomy-values-update-fix")) return;
        if (!props.saaTemplateFields || props.saaTemplateFields.length === 0) return;

        let saaObjects = Object.keys(flowExportSAATemplateFields.byId).map(x => flowExportSAATemplateFields.byId[x]);

        let transformObjects = saaObjects.filter(x => x.FlowItemId == props.flowItemId);

        //updates json with default values...
        transformObjects.forEach(row => {
            let shouldUpdate = false;
            let taxonomyObj = JSON.parse(row.ValueJSON);

            props.saaTemplateFields[0].SAASegmentFields.forEach(x => {
                if (x.SAASegmentField.DefaultValue != null) {
                    if (x.SAASegmentField.DefaultValue.includes("{date:")) {
                        const dateFormat = x.SAASegmentField.DefaultValue.substring(
                            x.SAASegmentField.DefaultValue.indexOf(":") + 1,
                            x.SAASegmentField.DefaultValue.indexOf("}")
                        );
                        let newDate = moment();
                        let thisDate = moment(newDate, dateFormat).format(dateFormat).toString();
                        if (taxonomyObj[x.SAASegmentField.FieldName] != thisDate) {
                            taxonomyObj[x.SAASegmentField.FieldName] = thisDate;
                        }
                    } else {
                        const company = companies.filter(z => z.CompanyID == currentCompanyId)[0];
                        const flow = props.flow;
                        const flowItem = props.flowItem;
                        if (x.SAASegmentField.DefaultValue.includes("{ProviderName}")) {
                            if (
                                thisDestination &&
                                thisDestination.item &&
                                thisDestination.item.CompanyFolder != "" &&
                                taxonomyObj[x.SAASegmentField.FieldName] != thisDestination.item.CompanyFolder
                            ) {
                                taxonomyObj[x.SAASegmentField.FieldName] = thisDestination.item.CompanyFolder;
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue.includes("{CompanyName}")) {
                            if (
                                company &&
                                company.CompanyFolder != "" &&
                                taxonomyObj[x.SAASegmentField.FieldName] != company.CompanyFolder
                            ) {
                                taxonomyObj[x.SAASegmentField.FieldName] = company.CompanyFolder;
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue.includes("{ActivationChannelId}")) {
                            if (
                                activationChannelId &&
                                taxonomyObj[x.SAASegmentField.FieldName] != activationChannelId
                            ) {
                                taxonomyObj[x.SAASegmentField.FieldName] = activationChannelId;
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue.includes("{FlowName}")) {
                            if (
                                flow &&
                                flow.FlowName != "" &&
                                taxonomyObj[x.SAASegmentField.FieldName] !=
                                    flow.FlowName.replace(/[^a-zA-Z0-9-_]/g, "").toUpperCase()
                            ) {
                                taxonomyObj[x.SAASegmentField.FieldName] = flow.FlowName.replace(
                                    /[^a-zA-Z0-9-_]/g,
                                    ""
                                ).toUpperCase();
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue.includes("{FlowId}")) {
                            if (flow && taxonomyObj[x.SAASegmentField.FieldName] != flow.FlowId) {
                                taxonomyObj[x.SAASegmentField.FieldName] = flow.FlowId;
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue.includes("{FlowItemId}")) {
                            if (flowItem && taxonomyObj[x.SAASegmentField.FieldName] != flowItem.FlowItemId) {
                                taxonomyObj[x.SAASegmentField.FieldName] = flowItem.FlowItemId;
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue.includes("{FlowItemName}")) {
                            if (
                                flowItem &&
                                flowItem.FlowItemName != "" &&
                                taxonomyObj[x.SAASegmentField.FieldName] !=
                                    flowItem.FlowItemName.replace(/[^a-zA-Z0-9-_]/g, "").toUpperCase()
                            ) {
                                taxonomyObj[x.SAASegmentField.FieldName] = flowItem.FlowItemName.replace(
                                    /[^a-zA-Z0-9-_]/g,
                                    ""
                                ).toUpperCase();
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue.includes("{ParentFlowItemName}")) {
                            const currentFlowItem = flowItems.find(x => x.FlowItemId == row.ParentFlowItemId);
                            if (
                                currentFlowItem &&
                                currentFlowItem.FlowItemName != "" &&
                                taxonomyObj[x.SAASegmentField.FieldName] !=
                                    currentFlowItem.FlowItemName.replace(/[^a-zA-Z0-9-_]/g, "").toUpperCase() &&
                                taxonomyObj[x.SAASegmentField.FieldName] == ""
                            ) {
                                taxonomyObj[x.SAASegmentField.FieldName] = currentFlowItem.FlowItemName.replace(
                                    /[^a-zA-Z0-9-_]/g,
                                    ""
                                ).toUpperCase();
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue.includes("{CPM}")) {
                            const parentFlowItem = flowItems.find(item => item.FlowItemId === row.ParentFlowItemId);
                            const cpmValue = getCPMValue(parentFlowItem, row.FlowItemId, row.ParentFlowItemId);
                            const fieldName = x.SAASegmentField.FieldName;

                            if (updateCPMField(taxonomyObj, row, fieldName, cpmValue)) {
                                shouldUpdate = true;
                            }
                        } else if (x.SAASegmentField.DefaultValue === "") {
                            // Do nothing
                        } else if (x.SAASegmentField.DefaultValue.includes("{")) {
                            // Do nothing
                        } else if (
                            !x.SAASegmentField.DefaultValue.includes("{") &&
                            taxonomyObj[x.SAASegmentField.FieldName] == ""
                        ) {
                            taxonomyObj[x.SAASegmentField.FieldName] = x.SAASegmentField.DefaultValue;
                            shouldUpdate = true;
                        }
                    }
                }
            });

            if (shouldUpdate) {
                handleFieldChange(row.FlowExportSAATemplateFieldId, "ValueJSON", JSON.stringify(taxonomyObj));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowExportSAATemplateFields]);

    const title = `Destination Configuration`;

    // Helper functions needed by renderSAAObject
    const handleFieldChange = useCallback(
        (Id: number, attributeName: string, value: string | number | boolean) =>
            dispatch(updateAttribute("flowExportSAATemplateFields", Id, attributeName, value)),
        [dispatch]
    );

    const getFieldValue = useCallback(
        (Id: number) => flowExportSAATemplateFields?.byId?.[Id]?.ValueJSON || "{}",
        [flowExportSAATemplateFields]
    );

    const DetermineFieldType = useCallback((data: any) => {
        switch (data.FieldType.toLowerCase()) {
            case "string":
                return "text";
            case "long":
                return "number";
            case "double":
                return "number";
            case "offsetdatetime":
                return "datetime-local";
            default:
                return "text";
        }
    }, []);

    const renderSAAObject = useCallback(
        (rowData: any, data: any, validationRules: any, IsVisible: boolean, lessenColumn: boolean) => {
            if (!IsVisible) {
                return null;
            }

            let jsonData = isImproveSAALayout
                ? JSON.parse(getFieldValue(rowData.FlowExportSAATemplateFieldId))
                : JSON.parse(rowData.ValueJSON);
            let newValue = jsonData;
            let isSegmentCode = false;
            let disabled = !data.IsEditable;
            let helperText = "";

            const currentFlowItem = flowItems.find(x => x.FlowItemId == rowData.ParentFlowItemId);
            const cpmValue = getCPMValue(currentFlowItem, props.flowItemId, rowData.ParentFlowItemId);

            // EMS-1301 For Samsumng taxonomy layouts, autocomplete with filter names
            if (
                enabledFeatures.includes("export-taxonomy-values-update-fix") &&
                data.FieldName.toLowerCase() === "segmentcode"
            ) {
                if (jsonData["SEGMENT_NAME"] === "" || jsonData["SEGMENT_DESCRIPTION"] === "") {
                    let newValue = jsonData;
                    newValue[data.FieldName] = currentFlowItem.FlowItemName;
                    newValue["SEGMENT_NAME"] = currentFlowItem.FlowItemName;
                    newValue["SEGMENT_DESCRIPTION"] = currentFlowItem.FlowItemName;
                    handleFieldChange(rowData.FlowExportSAATemplateFieldId, "ValueJSON", JSON.stringify(newValue));
                }

                isSegmentCode = true;
            }

            helperText =
                data.DefaultValue &&
                (data.DefaultValue.includes("{TrackingId}") || data.DefaultValue.includes("{AudienceId}")) &&
                disabled
                    ? "System generated on deployment."
                    : "";

            const fieldType = DetermineFieldType(data);
            const isDateTimeLocal = fieldType === "datetime-local";

            const handleFieldUpdate = (value: string) => {
                let newValue = { ...jsonData };

                // Compare the specific field value from Redux to the new value
                const currentValueJSON =
                    flowExportSAATemplateFields?.byId?.[rowData.FlowExportSAATemplateFieldId]?.ValueJSON;
                if (currentValueJSON) {
                    const currentParsed = JSON.parse(currentValueJSON);
                    if (currentParsed[data.FieldName] === value) {
                        return;
                    }
                    newValue = { ...currentParsed };
                }

                const next = { ...saveValidationWarnings };
                delete next[data.FieldName];

                // Validate with SAARule function
                validateSAARule(validationRules, data.FieldName, value, next);
                setSaveValidationWarnings(next);

                newValue[data.FieldName] = value;
                handleFieldChange(rowData.FlowExportSAATemplateFieldId, "ValueJSON", JSON.stringify(newValue));

                if (data.FieldName.toLowerCase() === "expirationtime") {
                    dispatch(syncScheduleEndDateWithExpiration(value));
                }
            };

            if (isSegmentCode && (!jsonData[data.FieldName] || jsonData[data.FieldName] == "")) {
                newValue[data.FieldName] = currentFlowItem ? currentFlowItem.FlowItemName : "";
            }

            const fieldKey = isDateTimeLocal
                ? `segment-general-${rowData.FlowExportSAATemplateFieldId}-${data.FieldName}`
                : "segment-general" + ++baseId;
            let textField = (
                <FormControl
                    style={
                        isImproveSAALayout
                            ? !lessenColumn
                                ? { width: "250px", margin: "0px 0px" }
                                : { width: "200px", margin: "0px 0px" }
                            : { width: "250px", margin: "0px 10px" }
                    }
                >
                    <TextField
                        id={fieldKey}
                        key={fieldKey}
                        disabled={disabled}
                        helperText={helperText}
                        defaultValue={jsonData[data.FieldName]}
                        type={fieldType}
                        required={data.Required}
                        inputProps={{ maxLength: 1050 }}
                        onBlur={e => handleFieldUpdate(e.target.value)}
                        onWheel={event => event.currentTarget.blur()}
                    />
                </FormControl>
            );

            if (data.DefaultValue == null) {
                return (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        {textField}
                    </FormControl>
                );
            }

            if (data.DefaultValue.includes("{numberRange:")) {
                //
                const range = data.DefaultValue.substring(
                    data.DefaultValue.indexOf(":") + 1,
                    data.DefaultValue.indexOf("}")
                ).split(",");
                const max = parseFloat(range[1]);
                const min = parseFloat(range[0]);
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <NumericalTextField
                            id={"saa-numberrange" + ++baseId}
                            key={"saa-numberrange" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            value={jsonData[data.FieldName]}
                            helperText={
                                !data.FieldName
                                    ? `Please enter a range between ${range[0]} and ${range[1]}.`
                                    : undefined
                            }
                            numberType="decimal"
                            type="number"
                            inputProps={{ min, max }}
                            onBlur={e => {
                                let newValue = jsonData;
                                let numberValue = parseFloat(e.target.value);
                                numberValue = numberValue >= max ? max : numberValue <= min ? min : numberValue;
                                newValue[data.FieldName] = numberValue.toString();

                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{date:")) {
                const dateFormat = data.DefaultValue.substring(
                    data.DefaultValue.indexOf(":") + 1,
                    data.DefaultValue.indexOf("}")
                );
                let newDate = moment();
                let thisDate = moment(newDate, dateFormat).format(dateFormat).toString();

                newValue[data.FieldName] = thisDate;

                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-date" + ++baseId}
                            key={"saa-date" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            helperText={!data.FieldName ? `Current date ${dateFormat}.` : undefined}
                            disabled={disabled}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{SegmentCode}")) {
                // Do nothing
            } else if (data.DefaultValue.includes("{ProviderName}")) {
                newValue[data.FieldName] = thisDestination?.item.CompanyFolder;
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-provider" + ++baseId}
                            key={"saa-provider" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            helperText={helperText}
                            inputProps={{ maxLength: 255 }}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{CompanyName}")) {
                const company = companies.filter(x => x.CompanyID == currentCompanyId)[0];
                newValue[data.FieldName] = company.CompanyFolder;
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-companyname" + ++baseId}
                            key={"saa-companyname" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            inputProps={{ maxLength: 255 }}
                            helperText={helperText}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{FlowName}")) {
                const flow = props.flow;
                newValue[data.FieldName] = flow ? flow.FlowName.replace(/[^a-zA-Z0-9-_]/g, "").toUpperCase() : "";
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-flowname" + ++baseId}
                            key={"saa-flowname" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            helperText={helperText}
                            inputProps={{ maxLength: 255 }}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{ActivationChannelId}")) {
                newValue[data.FieldName] = activationChannelId;
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-channelid" + ++baseId}
                            key={"saa-channelid" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            inputProps={{ maxLength: 255 }}
                            helperText={helperText}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{FlowId}")) {
                const flow = props.flow;
                newValue[data.FieldName] = flow ? flow.FlowId : "";
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-flowid" + ++baseId}
                            key={"saa-flowid" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            helperText={helperText}
                            inputProps={{ maxLength: 255 }}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{FlowItemId}")) {
                const flowItem = props.flowItem;
                newValue[data.FieldName] = flowItem.FlowItemId;
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-flowitemid" + ++baseId}
                            key={"saa-flowitemid" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            inputProps={{ maxLength: 255 }}
                            helperText={helperText}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{FlowItemName}")) {
                const flowItem = props.flowItem;
                newValue[data.FieldName] = flowItem.FlowItemName.replace(/[^a-zA-Z0-9-_]/g, "").toUpperCase();
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-flowitemname" + ++baseId}
                            key={"saa-flowitemname" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            helperText={helperText}
                            inputProps={{ maxLength: 255 }}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (String(data.DefaultValue).toLowerCase().includes("{parentflowitemid}")) {
                newValue[data.FieldName] = currentFlowItem ? currentFlowItem.FlowItemId : "";
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-parentflowitemid" + ++baseId}
                            key={"saa-parentflowitemid" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            inputProps={{ maxLength: 255 }}
                            helperText={helperText}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{ParentFlowItemName}")) {
                newValue[data.FieldName] =
                    jsonData[data.FieldName] && jsonData[data.FieldName] != ""
                        ? jsonData[data.FieldName]
                        : currentFlowItem
                          ? currentFlowItem.FlowItemName.replace(/[^a-zA-Z0-9-_]/g, "").toUpperCase()
                          : "";
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-parentitemname" + ++baseId}
                            key={"saa-parentitemname" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            helperText={helperText}
                            inputProps={{ maxLength: 255 }}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue.includes("{CPM}")) {
                if (!jsonData[data.FieldName] || jsonData[data.FieldName] === "") {
                    newValue[data.FieldName] = Number(cpmValue).toFixed(2);
                }
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-cpm" + ++baseId}
                            key={"saa-cpm" + ++baseId}
                            defaultValue={newValue[data.FieldName]}
                            disabled={disabled}
                            inputProps={{ maxLength: 255 }}
                            onBlur={e => {
                                let updatedValue = e.target.value;
                                let updatedJson = { ...jsonData, [data.FieldName]: updatedValue };
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(updatedJson)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (data.DefaultValue === "") {
                // Do nothing
            } else if (data.DefaultValue.includes("{")) {
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-other" + ++baseId}
                            key={"saa-other" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            helperText={disabled ? "System generated on deployment." : undefined}
                            inputProps={{ maxLength: 255 }}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value != "" ? e.target.value : "";
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            } else if (!data.DefaultValue.includes("{")) {
                textField = (
                    <FormControl
                        style={
                            isImproveSAALayout
                                ? !lessenColumn
                                    ? { width: "250px", margin: "0px 0px" }
                                    : { width: "200px", margin: "0px 0px" }
                                : { width: "250px", margin: "0px 10px" }
                        }
                    >
                        <TextField
                            id={"saa-nontransform" + ++baseId}
                            key={"saa-nontransform" + ++baseId}
                            defaultValue={jsonData[data.FieldName]}
                            disabled={disabled}
                            inputProps={{ maxLength: 255 }}
                            onBlur={e => {
                                let newValue = jsonData;
                                newValue[data.FieldName] = e.target.value != "" ? e.target.value : data.DefaultValue;
                                handleFieldChange(
                                    rowData.FlowExportSAATemplateFieldId,
                                    "ValueJSON",
                                    JSON.stringify(newValue)
                                );
                            }}
                            onWheel={event => event.currentTarget.blur()}
                        />
                    </FormControl>
                );
            }

            return (
                <FormControl
                    style={
                        isImproveSAALayout
                            ? !lessenColumn
                                ? { width: "250px", margin: "0px 0px" }
                                : { width: "200px", margin: "0px 0px" }
                            : { width: "250px", margin: "0px 10px" }
                    }
                >
                    {textField}
                </FormControl>
            );
        },
        [
            companies,
            currentCompanyId,
            thisDestination,
            activationChannelId,
            props.flow,
            props.flowItem,
            props.flowItemId,
            enabledFeatures,
            flowItems,
            getCPMValue,
            getFieldValue,
            DetermineFieldType,
            saveValidationWarnings,
            flowExportSAATemplateFields,
            handleFieldChange,
            isImproveSAALayout,
            dispatch,
        ]
    );

    // Validates a field value against a given set of validation rules.
    const validateSAARule = (
        validationRules: Array<{
            RuleType: string;
            Severity: string;
            ValidatorRegex?: string;
            Value?: string;
            Message: string;
        }>,
        fieldName: string,
        value: string,
        localWarnMsg: Record<string, string>
    ): void => {
        // Exit early if no validation rules exist (NORULES condition)
        if (!validationRules || validationRules.length === 0) {
            return;
        }

        // Pre-compile regex patterns for rules that use REGEX_VALIDATOR or REGEX
        const regexRules = validationRules
            .filter(rule => rule.RuleType === "REGEX_VALIDATOR" || rule.RuleType === "REGEX")
            .map(rule => ({ ...rule, compiledRegex: new RegExp(rule.ValidatorRegex!) }));

        validationRules.forEach(rule => {
            if (rule.Severity === "WARNING") {
                if (rule.RuleType === "REGEX_VALIDATOR" || rule.RuleType === "REGEX") {
                    const regexRule = regexRules.find(r => r.ValidatorRegex === rule.ValidatorRegex);
                    if (regexRule && !regexRule.compiledRegex.test(value)) {
                        localWarnMsg[fieldName] = `${rule.Severity}: ${fieldName} - ${rule.Message}`;
                    }
                } else {
                    const threshold = parseFloat(rule.Value!);
                    const numericValue = parseFloat(value);
                    // Ensure values are valid numbers before applying validation
                    if (!isNaN(threshold) && !isNaN(numericValue)) {
                        // If the validation rule condition is met, store the warning message
                        if (applyNumericValidation(rule.RuleType, numericValue, threshold)) {
                            localWarnMsg[fieldName] = `${rule.Severity}: ${fieldName} - ${rule.Message}`;
                        }
                    }
                    if (rule.RuleType === "IS_INT") {
                        if (applyNumericValidation(rule.RuleType, numericValue, threshold)) {
                            localWarnMsg[fieldName] = `${rule.Severity}: ${fieldName} - ${rule.Message}`;
                        }
                    }
                }
            }
        });
    };

    const renderItemName = (rowData: any) => {
        const currentFlowItem = flowItems.find(x => x.FlowItemId == rowData.ParentFlowItemId);
        if (currentFlowItem) {
            const itemName = currentFlowItem.FlowItemName;
            return (
                <FormControl
                    style={
                        isImproveSAALayout ? { width: "150px", margin: "0px" } : { width: "150px", margin: "0px 10px" }
                    }
                >
                    {itemName}
                </FormControl>
            );
        }
    };

    // Prepare data for TanStack Table
    const theseFields = useMemo(() => {
        let saaFields = Object.keys(flowExportSAATemplateFields.byId).map(x => flowExportSAATemplateFields.byId[x]);
        return saaFields.filter(x => x.FlowItemId == props.flowItemId);
    }, [flowExportSAATemplateFields, props.flowItemId]);

    // Detect when table data is ready and rendered
    useEffect(() => {
        if (isImproveSAALayout) {
            if (theseFields.length > 0 && props.saaTemplateFields?.[0]) {
                // Use RAF to ensure DOM has updated
                requestAnimationFrame(() => {
                    setIsTableReady(true);
                    // Notify parent component that table is ready
                    if (props.onTableReady) {
                        props.onTableReady();
                    }
                });
            } else {
                setIsTableReady(false);
            }
        }
    }, [isImproveSAALayout, theseFields.length, props.saaTemplateFields, props.onTableReady]);

    // Create TanStack Table columns
    const tanStackColumns = useMemo<ColumnDef<any>[]>(() => {
        const cols: ColumnDef<any>[] = [];

        if (props.saaTemplateFields != null && props.saaTemplateFields[0] != null) {
            // Item name column
            cols.push({
                id: "item",
                header: "Item",
                cell: info => renderItemName(info.row.original),
            });

            // Track visible fields
            let visibleCount = 1;
            const mainCols: ColumnDef<any>[] = [];
            const detailCols: ColumnDef<any>[] = [];

            props.saaTemplateFields[0].SAASegmentFields.forEach(x => {
                if (x.SAASegmentField.IsVisible) {
                    // Skip certain fields if feature enabled
                    if (
                        enabledFeatures.includes("export-taxonomy-values-update-fix") &&
                        (x.SAASegmentField.FieldName == "SEGMENT_NAME" ||
                            x.SAASegmentField.FieldName == "SEGMENT_DESCRIPTION")
                    ) {
                        return;
                    }
                    const isDetail = visibleCount >= localMaxVisibleCount;
                    const lessenColumn = isDetail && localMaxTableColumn === 1 && localMaxVisibleCount <= 3;
                    const colDef: ColumnDef<any> = {
                        id: x.SAASegmentField.FieldName,
                        header: x.SAASegmentField.DisplayName,
                        cell: info =>
                            renderSAAObject(
                                info.row.original,
                                x.SAASegmentField,
                                x.SAAValidationRules,
                                x.SAASegmentField.IsVisible,
                                lessenColumn
                            ),
                    };

                    if (visibleCount < localMaxVisibleCount) {
                        mainCols.push(colDef);
                    } else {
                        detailCols.push(colDef);
                    }
                    visibleCount++;
                }
            });

            cols.push(...mainCols);

            // Add expand/collapse button if there are detail columns
            if (detailCols.length > 0) {
                cols.push({
                    id: "expander",
                    header: "",
                    cell: ({ row }) => (
                        <Button
                            variant="text"
                            size="small"
                            style={{
                                fontSize: "12px",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                minWidth: "80px",
                            }}
                            onMouseDown={e => {
                                e.preventDefault(); // Prevent focus change

                                // Blur any focused element to trigger pending onBlur handlers
                                // This ensures TextField changes are saved before collapsing
                                if (document.activeElement instanceof HTMLElement) {
                                    document.activeElement.blur();
                                }

                                // Wait briefly for blur events to complete before toggling
                                // This is critical for datetime-local inputs where the picker
                                // needs time to commit the value and trigger onBlur
                                setTimeout(() => {
                                    // Always toggle the expansion state
                                    row.toggleExpanded();
                                }, 50);
                            }}
                        >
                            {row.getIsExpanded() ? "Less ▲" : "More ▶"}
                        </Button>
                    ),
                });
            }

            // Store detail columns for rendering
            (cols as any)._detailColumns = detailCols;
        }

        return cols;
    }, [
        isImproveSAALayout,
        props.saaTemplateFields,
        localMaxVisibleCount,
        localMaxTableColumn,
        enabledFeatures,
        setRenderingRows,
        renderItemName,
        renderSAAObject,
    ]);

    const legacyColumns = useMemo<ColumnDef<any>[]>(() => {
        const cols: ColumnDef<any>[] = [];
        if (!isImproveSAALayout && props.saaTemplateFields != null && props.saaTemplateFields[0] != null) {
            cols.push({
                id: "item",
                header: "Item",
                cell: info => renderItemName(info.row.original),
            });
            props.saaTemplateFields[0].SAASegmentFields.forEach(x => {
                if (x.SAASegmentField.IsVisible) {
                    if (
                        enabledFeatures.includes("export-taxonomy-values-update-fix") &&
                        (x.SAASegmentField.FieldName == "SEGMENT_NAME" ||
                            x.SAASegmentField.FieldName == "SEGMENT_DESCRIPTION")
                    ) {
                        return;
                    }
                    cols.push({
                        id: x.SAASegmentField.FieldName,
                        header: x.SAASegmentField.DisplayName,
                        cell: info =>
                            renderSAAObject(
                                info.row.original,
                                x.SAASegmentField,
                                x.SAAValidationRules,
                                x.SAASegmentField.IsVisible,
                                false
                            ),
                    });
                }
            });
        }
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isImproveSAALayout, props.saaTemplateFields, enabledFeatures, renderItemName, renderSAAObject]);

    const legacyTable = useReactTable({
        data: theseFields,
        columns: legacyColumns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: row => String(row.FlowExportSAATemplateFieldId),
    });

    // Initialize TanStack Table
    const table = useReactTable({
        data: theseFields,
        columns: tanStackColumns,
        state: {
            expanded,
        },
        onExpandedChange: setExpanded,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => (tanStackColumns as any)._detailColumns?.length > 0,
        getRowId: row => String(row.FlowExportSAATemplateFieldId), // Stable row ID to track expansion state
    });

    const renderImprovedSAAForm = () => {
        const detailCols = (tanStackColumns as any)._detailColumns || [];

        return (
            <>
                <style>
                    {`
                        @keyframes pulse {
                            0%, 100% {
                                opacity: 1;
                            }
                            50% {
                                opacity: 0.4;
                            }
                        }
                    `}
                </style>
                <div style={{ paddingTop: "20px" }}>
                    <Divider style={{ marginBottom: "20px" }} />
                    {!isTableReady ? (
                        <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
                            <div className="loading-spin" style={{ margin: "0 auto" }} />
                            <div style={{ marginTop: "16px" }}>Loading table data...</div>
                        </div>
                    ) : (
                        <div style={{ maxHeight: 400, overflow: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => (
                                                <th
                                                    key={header.id}
                                                    style={{
                                                        fontSize: 12,
                                                        textAlign: "left",
                                                        padding: "8px",
                                                        borderBottom: "1px solid #ddd",
                                                    }}
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext()
                                                          )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map(row => (
                                        <React.Fragment key={row.id}>
                                            <tr style={{ fontSize: 12, padding: "2px" }}>
                                                {row.getVisibleCells().map(cell => (
                                                    <td key={cell.id} style={{ padding: "2px 8px" }}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                            {row.getIsExpanded() && detailCols.length > 0 && (
                                                <tr>
                                                    <td colSpan={row.getVisibleCells().length}>
                                                        {renderingRows.has(row.id) ? (
                                                            <div
                                                                style={{
                                                                    padding: "20px 0px",
                                                                    backgroundColor: "#f9f9f9",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    gap: "18px",
                                                                }}
                                                            >
                                                                {/* Skeleton loader */}
                                                                {Array.from({
                                                                    length: Math.min(detailCols.length, 4),
                                                                }).map((_, i) => (
                                                                    <div
                                                                        key={i}
                                                                        style={{
                                                                            display: "flex",
                                                                            gap: "20px",
                                                                            alignItems: "center",
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                width: "250px",
                                                                                height: "16px",
                                                                                backgroundColor: "#e0e0e0",
                                                                                borderRadius: "4px",
                                                                                animation:
                                                                                    "pulse 1.5s ease-in-out infinite",
                                                                            }}
                                                                        />
                                                                        <div
                                                                            style={{
                                                                                flex: 1,
                                                                                height: "40px",
                                                                                backgroundColor: "#e0e0e0",
                                                                                borderRadius: "4px",
                                                                                animation:
                                                                                    "pulse 1.5s ease-in-out infinite",
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {
                                                                    <div
                                                                        style={{
                                                                            width: "98%",
                                                                            maxWidth: "98%",
                                                                            padding: "20px 8px",
                                                                            backgroundColor: "#f9f9f9",
                                                                            display: "flex",
                                                                            flexDirection: "row",
                                                                            gap: "30px",
                                                                        }}
                                                                    >
                                                                        {/* Two-column layout */}
                                                                        <div
                                                                            style={{
                                                                                flex: 1,
                                                                                display: "flex",
                                                                                flexDirection: "column",
                                                                                gap: "18px",
                                                                            }}
                                                                        >
                                                                            {detailCols
                                                                                .slice(
                                                                                    0,
                                                                                    Math.ceil(detailCols.length / 2)
                                                                                )
                                                                                .map((col, i) => (
                                                                                    <div
                                                                                        key={i}
                                                                                        style={{
                                                                                            display: "flex",
                                                                                            gap: "10px",
                                                                                        }}
                                                                                    >
                                                                                        <label
                                                                                            style={{
                                                                                                width:
                                                                                                    localMaxTableColumn ==
                                                                                                    2
                                                                                                        ? "250px"
                                                                                                        : "200px",
                                                                                                fontWeight: 500,
                                                                                                fontSize: "12px",
                                                                                            }}
                                                                                        >
                                                                                            {typeof col.header ===
                                                                                            "string"
                                                                                                ? col.header
                                                                                                : ""}
                                                                                            :
                                                                                        </label>
                                                                                        <div style={{ flex: 1 }}>
                                                                                            {col.cell &&
                                                                                            typeof col.cell ===
                                                                                                "function"
                                                                                                ? col.cell({
                                                                                                      row: {
                                                                                                          original:
                                                                                                              row.original,
                                                                                                      },
                                                                                                  } as any)
                                                                                                : null}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                flex: 1,
                                                                                display: "flex",
                                                                                flexDirection: "column",
                                                                                gap: "18px",
                                                                            }}
                                                                        >
                                                                            {detailCols
                                                                                .slice(Math.ceil(detailCols.length / 2))
                                                                                .map((col, i) => (
                                                                                    <div
                                                                                        key={i}
                                                                                        style={{
                                                                                            display: "flex",
                                                                                            gap: "10px",
                                                                                        }}
                                                                                    >
                                                                                        <label
                                                                                            style={{
                                                                                                width:
                                                                                                    localMaxTableColumn ==
                                                                                                    2
                                                                                                        ? "250px"
                                                                                                        : "200px",
                                                                                                fontWeight: 500,
                                                                                                fontSize: "12px",
                                                                                            }}
                                                                                        >
                                                                                            {typeof col.header ===
                                                                                            "string"
                                                                                                ? col.header
                                                                                                : ""}
                                                                                            :
                                                                                        </label>
                                                                                        <div style={{ flex: 1 }}>
                                                                                            {col.cell &&
                                                                                            typeof col.cell ===
                                                                                                "function"
                                                                                                ? col.cell({
                                                                                                      row: {
                                                                                                          original:
                                                                                                              row.original,
                                                                                                      },
                                                                                                  } as any)
                                                                                                : null}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                        </div>
                                                                    </div>
                                                                }
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderSAAForm = () => (
        <div style={{ paddingTop: "20px" }}>
            <Divider style={{ marginBottom: "20px" }} />
            <div style={{ maxHeight: 400, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        {legacyTable.getHeaderGroups().map(hg => (
                            <tr key={hg.id}>
                                {hg.headers.map(header => (
                                    <th
                                        key={header.id}
                                        style={{
                                            fontSize: 12,
                                            textAlign: "left",
                                            padding: "8px",
                                            borderBottom: "1px solid #ddd",
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {legacyTable.getRowModel().rows.map(row => (
                            <tr key={row.id} style={{ fontSize: 12 }}>
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        style={{ padding: "14px 8px", borderBottom: "1px solid #e0e0e0" }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const warn = Object.keys(saveValidationWarnings).length ? saveValidationWarnings : validationMessages.warnings;
    return (
        <>
            <div className="flow-edit-header">
                <div style={{ flexGrow: 1, position: "relative" }}>
                    <p>{title}</p>
                </div>
            </div>
            <Divider style={{ margin: "20px auto" }} />
            <Paper style={{ marginTop: "30px", padding: "0px 20px 20px 20px", width: "100%" }}>
                <div className="m-t-md">{isImproveSAALayout ? renderImprovedSAAForm() : renderSAAForm()}</div>
            </Paper>
            <div style={{ marginTop: "10px" }}>
                {Object.values(warn).map((msg, index) => (
                    <div key={`warning-${index}`} style={{ color: "orange" }}>
                        {msg}
                    </div>
                ))}
            </div>
            <Divider style={{ margin: "40px auto 20px auto" }} />
        </>
    );
};
// Selector functions
type FlowExportSelector<S> = (state: IAppState, props: Props) => S;
type FlowExportDestinationSelector<S> = (state: IAppState) => S;
export const makeGetExportDestinations = (): FlowExportDestinationSelector<Array<IAdvertisersByCompany>> =>
    // @ts-ignore
    createSelector(
        (state: IAppState) => state.vars.destinations,
        (destinations: Array<IAdvertisersByCompany>): Array<IAdvertisersByCompany> =>
            destinations ? destinations.filter(x => x.AcceptFlag == "Y") : []
    );

export const makeGetFlowExport = (): FlowExportSelector<FlowExport | undefined> =>
    // @ts-ignore
    createSelector(
        (state: IAppState) => getFlowExportsByFlowItemId(state),
        (_, props: Props) => props.flowItemId,
        (flowExportsByFlowItemId: FlowExportsByItemId, flowItemId: number): FlowExport | undefined =>
            flowExportsByFlowItemId[flowItemId]
    );
export default FlowExportSAAFileBasedFields;
